import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import '../static/css/main.css';
import './documents/documents.css';
import './shared/companyPicker.css';
import HomeAnimatedIcon from './icons/HomeAnimatedIcon';
import DocumentsAnimatedIcon from './icons/DocumentsAnimatedIcon';
import ChatAnimatedIcon from './icons/ChatAnimatedIcon';
import TasksAnimatedIcon from './icons/TasksAnimatedIcon';
import ProfileAnimatedIcon from './icons/ProfileAnimatedIcon';
import { useAccountBadgesContext } from '../context/AccountBadgesContext';
import { useSectionMenuResetContext } from '../context/SectionMenuResetContext';
import { formatBadgeCount } from '../utils/badgeCounts';
import { hasMultipleAccounts, isDelegatedCompanyAccess } from '../utils/accountUtils';
import { selectUserId, selectUsers, selectCurrentUser } from './login/loginSlice';
import { applyMarkCompanyChatUnread } from '../utils/markCompanyChatUnreadClient';

const LONG_PRESS_MS = 500;

const MENU_ITEMS = [
  { path: '/client', label: 'Главная', Icon: HomeAnimatedIcon, size: 22, badgeKey: null },
  { path: '/documents', label: 'Документы', Icon: DocumentsAnimatedIcon, size: 24, badgeKey: 'documents' },
  { path: '/tasks', label: 'Задачи', Icon: TasksAnimatedIcon, size: 24, badgeKey: 'tasks' },
  { path: '/chat', label: 'Чат', Icon: ChatAnimatedIcon, size: 24, badgeKey: 'chat' },
  { path: '/profile', label: 'Профиль', Icon: ProfileAnimatedIcon, size: 27, badgeKey: null },
];

function Menu({ handleClickUser }) {
  const location = useLocation();
  const navigate = useNavigate();
  const userId = useSelector(selectUserId);
  const users = useSelector(selectUsers);
  const currentUser = useSelector(selectCurrentUser);
  const multiple = hasMultipleAccounts(users);
  const { totals, hasEmptyBankPack, byUserId, patchChatBadge, refresh: refreshAccountBadges } = useAccountBadgesContext();
  const { tryReset } = useSectionMenuResetContext() || {};
  const [showBankPromptModal, setShowBankPromptModal] = React.useState(false);
  const [chatTabContextMenu, setChatTabContextMenu] = useState(null);
  const bankPromptDismissedRef = React.useRef(false);
  const chatTabLongPressTimerRef = useRef(null);
  const chatTabLongPressTriggeredRef = useRef(false);

  React.useEffect(() => {
    bankPromptDismissedRef.current = false;
  }, [totals.documents]);

  React.useEffect(() => {
    const delegatedNoBank =
      isDelegatedCompanyAccess(currentUser) && !currentUser?.access_bank;
    if (delegatedNoBank) {
      setShowBankPromptModal(false);
      return;
    }
    if (hasEmptyBankPack && !bankPromptDismissedRef.current) {
      setShowBankPromptModal(true);
    }
  }, [hasEmptyBankPack, currentUser]);

  useEffect(() => {
    if (!chatTabContextMenu) return undefined;
    const close = () => setChatTabContextMenu(null);
    const timer = setTimeout(() => {
      document.addEventListener('click', close);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', close);
    };
  }, [chatTabContextMenu]);

  const clearChatTabLongPress = () => {
    if (chatTabLongPressTimerRef.current) {
      clearTimeout(chatTabLongPressTimerRef.current);
      chatTabLongPressTimerRef.current = null;
    }
  };

  const openChatTabMarkUnreadMenu = (clientX, clientY) => {
    if (multiple) return;
    setChatTabContextMenu({ x: clientX, y: clientY });
  };

  const handleChatTabContextMenu = (e) => {
    if (multiple) return;
    e.preventDefault();
    e.stopPropagation();
    openChatTabMarkUnreadMenu(e.clientX, e.clientY);
  };

  const handleChatTabTouchStart = (e) => {
    if (multiple) return;
    chatTabLongPressTriggeredRef.current = false;
    const touch = e.touches?.[0];
    if (!touch) return;
    clearChatTabLongPress();
    const { clientX, clientY } = touch;
    chatTabLongPressTimerRef.current = setTimeout(() => {
      chatTabLongPressTriggeredRef.current = true;
      openChatTabMarkUnreadMenu(clientX, clientY);
    }, LONG_PRESS_MS);
  };

  const handleChatTabMarkUnreadConfirm = async () => {
    setChatTabContextMenu(null);
    await applyMarkCompanyChatUnread({
      userId,
      getCurrentBadge: (id) => byUserId?.[id]?.chat,
      patchChatBadge,
      refreshAccountBadges,
    });
  };

  const menuStyles = {
    listStyleType: 'none',
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {showBankPromptModal && (
        <div className="bank-prompt-overlay" role="dialog" aria-modal="true">
          <div className="bank-prompt-card">
            <p className="bank-prompt-title manrope-bold">Просьба загрузить банковские выписки</p>
            <p className="bank-prompt-subtitle manrope-400">за прошедший месяц</p>
            <div className="bank-prompt-buttons">
              <button
                type="button"
                className="bank-prompt-btn bank-prompt-btn-cancel manrope-bold"
                onClick={() => {
                  bankPromptDismissedRef.current = true;
                  setShowBankPromptModal(false);
                }}
              >
                Отменить
              </button>
              <button
                type="button"
                className="bank-prompt-btn bank-prompt-btn-primary manrope-bold"
                onClick={() => {
                  bankPromptDismissedRef.current = true;
                  setShowBankPromptModal(false);
                  navigate('/documents', { state: { openBank: true } });
                }}
              >
                Загрузить
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={`menu_container ${(location.pathname === "/chat" ? 'menu_mobile': '' )}`}>
      <ul style={menuStyles} className="d-f cntr_ui_menu">
        {MENU_ITEMS.map((item) => {
          const active = isActive(item.path);
          const { Icon } = item;
          const badgeValue = item.badgeKey ? formatBadgeCount(totals[item.badgeKey]) : null;
          const isChatItem = item.path === '/chat';
          const chatMarkUnreadHandlers = isChatItem && !multiple
            ? {
                onContextMenu: handleChatTabContextMenu,
                onTouchStart: handleChatTabTouchStart,
                onTouchEnd: clearChatTabLongPress,
                onTouchMove: clearChatTabLongPress,
                onTouchCancel: clearChatTabLongPress,
                title: 'Удержите или ПКМ — сделать непрочитанным',
              }
            : {};
          return (
          <li key={item.path} className='menu_item'>
            <Link
            className='txt-decor-none menu_item_link'
              to={item.path}
              onClick={(e) => {
                if (isChatItem && chatTabLongPressTriggeredRef.current) {
                  chatTabLongPressTriggeredRef.current = false;
                  e.preventDefault();
                  return;
                }
                if (active && tryReset?.(item.path)) {
                  e.preventDefault();
                }
              }}
              >
            <span className="menu-icon-badge-wrap" {...chatMarkUnreadHandlers}>
              <Icon
                isActive={active}
                size={item.size}
              />
              {badgeValue && (
                <span className="menu-unread-badge">{badgeValue}</span>
              )}
            </span>
            <div
              className={`menu-link manrope-superbold ${active ? 'active' : ''}`}
            >
              {item.label}
            </div>
            </Link>
          </li>
          );
        })}
      </ul>
      </div>
      {chatTabContextMenu && (
        <div
          className="company-picker-context-menu"
          style={{ left: chatTabContextMenu.x, top: chatTabContextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="company-picker-context-menu-item"
            onClick={handleChatTabMarkUnreadConfirm}
          >
            Сделать непрочитанным
          </button>
        </div>
      )}
    </>
  );
}

export default Menu;
